import React, { createContext, useContext } from "react";

/**
 * File I/O seam for the upload/display element renderers.
 *
 * The renderers (FileUpload, ImageUpload, Signature, FileDownload,
 * DisplayImage) own all UI + state; the host app injects the three
 * environment-specific async calls below. This lets the public
 * form-fill host (anonymous, publicSlug + secretToken) and the
 * authenticated web host share one set of components while each keeps
 * its own GraphQL contract and auth.
 */

/** A resolved, fetchable file reference. Renderers only need url + name. */
export interface ResolvedFile {
  name: string | null;
  /** Full-res (download / view-original) URL. */
  url: string | null;
  /**
   * Small, display-sized preview URL (the media-worker thumbnail). Renderers
   * show this inline and keep `url` for download / zoom. Null until the
   * thumbnail is generated — fall back to `url`.
   */
  thumbnailUrl?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
  /** Human display size (e.g. "2.4 MB") when the host provides one. */
  sizeLabel?: string | null;
}

export interface PresignedUpload {
  /** S3 (or compatible) URL the client PUTs the file bytes to. */
  signedUrl: string;
  /** The created attachment's domain id, stored in the element value. */
  fileId: number;
  /** Server-confirmed file name. */
  name: string;
}

export interface FormFileTransport {
  /** Create the attachment row + presigned PUT url for a pending upload. */
  createPresignedUpload(args: {
    filename: string;
    contentType: string | null;
    sizeBytes: number | null;
  }): Promise<PresignedUpload>;
  /** Resolve a file uploaded into the current fill result (by attachment id). */
  fetchResultFileUrl(
    fileId: number,
    opts?: { download?: boolean },
  ): Promise<ResolvedFile | null>;
  /** Resolve a file the form designer attached at design time. */
  fetchPublicFileUrl(fileId: number): Promise<ResolvedFile | null>;
  /**
   * Optional: resolve a signed URL for an image resized to `width` px (longest
   * edge), generated on demand + cached by the media-worker. Powers the
   * lightbox "view bigger" size — the renderer requests a viewport-sized image
   * rather than the full-res original. `opts.design` targets a design-time
   * image (DisplayImage) rather than an uploaded answer file. Returns null when
   * unavailable (non-image, host without the image service) — callers fall back
   * to the full-res `url`.
   */
  fetchSizedUrl?(
    fileId: number,
    width: number,
    opts?: { design?: boolean },
  ): Promise<string | null>;
  /**
   * Optional: lazily establish the fill result this upload writes into.
   *
   * The public host creates its draft FormDocumentResult on demand here, so a
   * filler whose very first action is a file/image/signature upload still
   * works — otherwise the upload would be gated on a result row that only an
   * autosave (a separate field change) ever creates, which never happens on
   * an upload-first form. Resolves `true` once a result exists, or `false`
   * when it can't be made ready yet (e.g. a required CAPTCHA is unsolved), in
   * which case the upload is not attempted. Hosts that create the result
   * eagerly can omit this.
   */
  ensureReady?: () => Promise<boolean>;
  /**
   * Optional synchronous resolve, for server-side rendering (the PDF service).
   * The file hooks resolve URLs in a `useEffect`, which never runs under
   * `renderToStringWithData` — so an SSR host pre-resolves every referenced
   * file and returns it here, letting the renderers show the image in the one
   * synchronous pass. Client hosts omit it (initial state stays null, then the
   * async fetch fills in as today).
   */
  peekFile?(
    fileId: number,
    opts?: { download?: boolean },
  ): ResolvedFile | null;
  /**
   * Optional fire-and-forget hook called after a file finishes uploading, so
   * the host can queue preview-thumbnail generation. Errors are swallowed.
   */
  onUploaded?(fileId: number): void;
}

const notConfigured = (): never => {
  throw new Error(
    "No FormFileTransportProvider in the tree — file/upload elements " +
      "require a host-supplied FormFileTransport.",
  );
};

const FormFileTransportContext = createContext<FormFileTransport>({
  createPresignedUpload: notConfigured,
  fetchResultFileUrl: notConfigured,
  fetchPublicFileUrl: notConfigured,
});

export const FormFileTransportProvider: React.FC<{
  value: FormFileTransport;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <FormFileTransportContext.Provider value={value}>
    {children}
  </FormFileTransportContext.Provider>
);

export const useFormFileTransport = (): FormFileTransport =>
  useContext(FormFileTransportContext);

/**
 * Lets a host know while any file element is still uploading, so a save
 * that would otherwise send the old file reference can wait. The default
 * provider ignores the calls; hosts that care (the amend dialog) count them.
 */
export interface UploadActivity {
  begin: () => void;
  end: () => void;
}

const UploadActivityContext = createContext<UploadActivity>({
  begin: () => {},
  end: () => {},
});

export const UploadActivityProvider: React.FC<{
  value: UploadActivity;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <UploadActivityContext.Provider value={value}>
    {children}
  </UploadActivityContext.Provider>
);

export const useUploadActivity = (): UploadActivity =>
  useContext(UploadActivityContext);
